import { Callback, Inventory, WeaponCharacteristics } from "game-common/models"
import { BitmapText, Circle, Graphics, Text } from "pixi.js"

import { SpriteMeta } from "../entity_rendering_manager"
import { GuiManager } from "../gui_manager"
import { BasicSprite } from "../sprites/basic_sprite"
import { DynamicText } from "./dynamic_text"
import { TextRenderer } from "../text/text_renderer"
import { disposeTextTextures } from "../text/dispose_text"

// Horizontal distance between slot centres, and the radius of each slot's press target.
//
// The radius is comfortably larger than the 15 the selected-slot highlight draws, and short of
// half the pitch so two neighbours can never overlap. It is in the widget's own coordinates, so
// the touch scaling applied to the whole widget on a phone grows this with everything else.
//
// The pitch was widened from 50 to make room for the radius rather than the other way round:
// measured on a Pixel 4a, the old 22 reached about 35 device pixels, which is ~26 CSS px of target
// against the ~44 a thumb wants. Half the pitch is the hard ceiling on the radius, so the slots
// had to move apart first. There is ample width for it -- three slots on a 640-wide stage.
export const SLOT_PITCH = 58
const SLOT_HIT_RADIUS = 27

export class EquippedWidget extends Graphics {
    guiManager: GuiManager

    constructor(guiManager: GuiManager) {
        super()

        this.guiManager = guiManager
    }

    renderSlot = (label: string, spriteMeta: SpriteMeta, selected: boolean, callback: Callback, amt?: number) => {
        const slot = new Graphics()

        const item = new BasicSprite(spriteMeta)
        slot.addChild(item)

        // One uniform, circular press target per slot, rather than each item's own sprite bounds.
        //
        // Hanging the handler off the sprite made a slot exactly as easy to hit as its art is big:
        // a shield filled the slot and a dagger was a few pixels of blade on the diagonal, so the
        // same gesture picked one reliably and missed the other. Which item is in a slot should not
        // change how hard it is to select. A circle rather than a box because the slot READS as a
        // circle -- that is the shape the selected highlight draws -- so the target matches what is
        // on screen, and a corner tap that looks outside the slot does not select it.
        //
        // Sits outside the highlight's own radius but inside half the slot pitch, so neighbouring
        // targets cannot overlap and steal each other's taps.
        slot.interactive = true
        slot.cursor = "pointer"
        slot.hitArea = new Circle(0, 0, SLOT_HIT_RADIUS)
        slot.on("pointerdown", callback)

        if (amt !== undefined) {
            const amtLabel = new DynamicText(`${amt}`, { font: "ChatFont" })
            amtLabel.y = 5
            slot.addChild(amtLabel)
        }

        const labelObject = new TextRenderer().size("tiny").render(label) as Text
        labelObject.x = -20
        labelObject.y = -30
        if (selected) {
            labelObject.tint = 0x00ff00
            slot.beginFill(0x00ff00, 0.25)
            slot.drawCircle(0, 0, 15)
            slot.endFill()
        }
        slot.addChild(labelObject)

        return slot
    }

    update = (inventory: Inventory, slotCallback: (itemId: string) => void): Record<number, Callback> => {
        // clear all -- dispose per-instance Text textures first, else slot labels/quantities leak a
        // canvas texture each on every inventory update (this rebuilds constantly during gameplay as
        // items are gathered/equipped). Shared item-sprite textures are left intact.
        ;[...this.children].forEach(disposeTextTextures)
        this.removeChildren()

        const baseMeta: SpriteMeta = {
            animated: false,
            color: 0xffffff,
            name: "",
            scale: 1.0,
        }

        const slotCallbacks: Record<number, Callback> = {}

        inventory.items
            ?.filter(item => WeaponCharacteristics[item.itemType]?.slotVisible && item.readied)
            // .sort( (a, b) => WeaponCharacteristics[a.itemType].equipOrder - WeaponCharacteristics[b.itemType].equipOrder)
            .map((item, i) => {
                const { itemType } = item
                const scale = 1
                const name = item.itemTypeMeta.spriteId || `${item.itemTypeMeta.type}-${item.itemTypeMeta.color}.png`
                const shortcutKey = `${i + 1}`
                const callback = () => slotCallback(item.id)

                const slot = this.renderSlot(
                    shortcutKey,
                    {
                        ...baseMeta,
                        color: item.active ? 0x00ff00 : baseMeta.color,
                        name,
                        scale,
                    },
                    item.active,
                    callback,
                    item.quantity > 0 ? item.quantity : undefined,
                )

                slotCallbacks[i + 1] = callback

                slot.x = i * SLOT_PITCH
                this.addChild(slot)
            })

        return slotCallbacks
    }
}
